import React from 'react';
import { ExternalLink, Link } from '../router';
import { BOOKING_URL } from '../links';
import { useOpenNewsletter } from '../newsletter';
import EdgeProcess from '../components/EdgeProcess';

const offers = [
  {
    name: 'Complimentary Performance Strategy Session',
    description: "A private 1:1 call to map what's blocking your performance.",
    to: '/work-with-me#session',
  },
  { name: 'Reclaim Your Cognitive Edge', description: 'My signature 1:1 coaching program.', to: '/work-with-me#signature' },
  {
    name: 'The Focus Reboot',
    description: 'A 5-day 1:1 intensive, 20 minutes a day, for a fast concentrated start.',
    to: '/work-with-me#focus-reboot',
  },
  { name: 'The Neuro Recharge Retreat', description: 'A 5-day in-person reset. Dates coming soon.', to: '/work-with-me#retreat' },
  { name: 'Walk to Possibilities', description: 'A 30-day self-paced body reset.', href: 'https://www.walk2possibilities.com/' },
  {
    name: 'Premium Coaching Sessions',
    description: 'Kinesiology, CranioSacral, herbal medicine, NLP, MRI interpretation.',
    to: '/contact',
  },
  {
    name: 'NLP Leadership Session',
    description: 'One focused session to break a leadership problem cycle.',
    to: '/work-with-me#nlp',
  },
  { name: 'Joint Projects', description: 'Speaking and presence workshops with expert partners.', to: '/work-with-me#joint' },
];

const Home: React.FC = () => {
  const openNewsletter = useOpenNewsletter();

  return (
    <>
      {/* Section 1 — Hero */}
      <section className="section hero">
        <div className="container hero__grid">
          <div className="hero__content">
            <h1>Hanna Kinez</h1>
            <p className="role-line">
              Executive Performance Coach · Applied Kinesiologist · PT · Medical Herbalist · Eastern &amp; Western Medicine
            </p>
            <h2 className="hero__hook">
              You're still performing.
              <br />
              But it costs more than it used to.
            </h2>
            <p>
              You built the career. The team. The reputation. But somewhere along the way, the energy, the focus, the drive. They
              all started needing more effort to maintain.
            </p>
            <p>Every lab says you're fine. You know you're not. That gap is where I work.</p>
            <p className="hero__punchline">
              Men hire me to reclaim cognitive edge, biological vitality, and peak performance.
            </p>
            <ExternalLink href={BOOKING_URL} className="btn btn-primary">
              Book a Complimentary Session
            </ExternalLink>
          </div>
          <div className="hero__image">
            <img src="/anna3.jpg" alt="Hanna Kinez" />
          </div>
        </div>
      </section>

      {/* Section 2 — Introduction + credibility */}
      <section className="intro">
        <div className="container">
          <p className="intro__statement">
            I'm an executive performance coach certified across Eastern and Western medicine. I
            guide <strong>engineers and technical leaders</strong> to restore the <strong>cognitive edge</strong> and{' '}
            <strong>physical vitality</strong> that slipped away — by finding what's <strong>failing</strong> in your body and
            rebuilding the <strong>system</strong> underneath it.
          </p>

          <div className="intro__stats">
            <div>
              <strong>15</strong>
              <span>years</span>
            </div>
            <div>
              <strong>12</strong>
              <span>countries</span>
            </div>
            <div>
              <strong>1,000+</strong>
              <span>professionals guided</span>
            </div>
            <div>
              <strong>10,000+</strong>
              <span>reached through workshops</span>
            </div>
            <div>
              <strong>15,000</strong>
              <span>at We Are Developers Berlin</span>
            </div>
          </div>

          <p className="intro__clients">Clients from Google · Microsoft · Cisco · NetApp</p>
          <p className="intro__certs">
            <span>Certified in:</span> Applied Kinesiology · Medical Herbalism · NLP · CranioSacral Therapy · MRI Interpretation ·
            Blood Work Interpretation · Tibetan Medicine · Physiotherapy (PT)
          </p>
        </div>
      </section>

      {/* Section 3 — Where you are now */}
      <section className="section section--lavender">
        <div className="container container--narrow">
          <h2>What I Hear in the First Call</h2>
          <p className="lead">You can still carry the load. You just can't do it with ease anymore.</p>
          <ul className="symptoms">
            <li>Ideas stuck in your head, and no energy left to build them.</li>
            <li>Focus that used to hold for hours now slips by mid-morning.</li>
            <li>Sleep that doesn't recharge you.</li>
            <li>Neck and back tension that outlasts every specialist visit.</li>
            <li>Your drive, at work and at home, gone quiet.</li>
          </ul>
          <p className="lead">
            None of it shows up in a blood test. <strong>All of it shows up in your work.</strong>
          </p>
        </div>
      </section>

      {/* Section 4 — What I Do */}
      <section className="section">
        <div className="container container--narrow">
          <h2>What I Do</h2>
          <h3>I Give You Back Ownership of Your Health</h3>
          <p className="lead">
            <strong>I don't chase symptoms.</strong>
          </p>
          <p>
            I find what's <strong>failing</strong> across your whole system and rebuild it, so staying vital,
            sharp, and productive becomes easy to maintain on your own. My work balances your health and returns{' '}
            <strong>ownership of it</strong> to you: a sustainable system that protects your energy and{' '}
            <strong>restores and protects your cognitive abilities</strong>.
          </p>
          <p>
            When your biology works, your <strong>focus, creativity, and income</strong> follow. I'm the founder of the{' '}
            <strong>KinezioSomatic method</strong>, built on the <strong>Square of Health</strong>, a four-part map of what drives
            your performance: <strong>physical, biochemical, energy, and mental / psychosocial</strong>.
          </p>
          <Link to="/about" className="btn btn-primary">
            Read My Full Story
          </Link>
        </div>
      </section>

      {/* Section 5 — Signature Program */}
      <section className="section section--alt" id="signature">
        <div className="container container--narrow">
          <h2>Reclaim Your Cognitive Edge</h2>
          <h3>The Square of Health: Eastern and Western medicine in one map, to keep you sharp for the long run.</h3>
          <p className="lead">The EDGE process:</p>
          <EdgeProcess />
          <ExternalLink href={BOOKING_URL} className="btn btn-primary">
            Book a Call About the Program
          </ExternalLink>
        </div>
      </section>

      {/* Section 6 — Work With Me overview */}
      <section className="section">
        <div className="container center">
          <h2>Work With Me</h2>
          <div className="card-grid">
            {offers.map((offer) => {
              const content = (
                <>
                  <h3>{offer.name}</h3>
                  <p>{offer.description}</p>
                </>
              );
              return offer.href ? (
                <ExternalLink key={offer.name} href={offer.href} className="card card--link">
                  {content}
                </ExternalLink>
              ) : (
                <Link key={offer.name} to={offer.to!} className="card card--link">
                  {content}
                </Link>
              );
            })}
          </div>
          <Link to="/work-with-me" className="btn btn-primary">
            See How We Can Work Together
          </Link>
        </div>
      </section>

      {/* Section 7 — Complimentary Resources teaser */}
      <section className="section section--base">
        <div className="container center">
          <h2>Complimentary Resources</h2>
          <p>Two complimentary guides to start today:</p>
          <div className="resource-columns">
            <div>
              <h3>Your Nervous System Under Load</h3>
              <p>
                A 2-minute self-assessment: are you regulated, under strain, or overloaded? With next steps for each.
              </p>
            </div>
            <div>
              <h3>The Context Switching Tax</h3>
              <p>What constant task-switching is costing your focus, and a 3-step fix.</p>
            </div>
          </div>
          <p className="resource-more">
            And many more on the <Link to="/resources">Resources page</Link>: the KinezioLetter, where I share the tools I use
            with private clients, videos, and the Herb Library.
          </p>
          <Link to="/resources" className="btn btn-primary">
            Get the Complimentary Guides
          </Link>
        </div>
      </section>

      {/* Section 8 — What Clients Say */}
      <section className="section">
        <div className="container container--narrow">
          <h2 className="center">What Clients Say</h2>
          <blockquote className="testimonial">
            <p>"I had accepted that I was getting slow because I was growing old. That wasn't the problem."</p>
            <footer>
              — <strong>Marek</strong>, Senior Engineer. In a five-day <strong>Focus Reboot</strong> he went from under 8 minutes of
              focus to <strong>45+</strong>, from sleep <strong>4/10 to 7/10</strong>, and broke a chronic procrastination pattern.
            </footer>
          </blockquote>
          <blockquote className="testimonial">
            <p>
              "I came in skeptical. I am an engineer. What Hanna showed me was not wellness. It was a system. Three months later I
              am clearer than I have been in five years. I get more done in less time, and I'm present with my family in
              the evenings instead of just physically there."
            </p>
            <footer>
              — <strong>Jeff J.</strong>, Senior Engineer
            </footer>
          </blockquote>
        </div>
      </section>

      {/* Section 9 — Closing CTA / KinezioLetter */}
      <section className="section section--dark">
        <div className="container container--narrow center">
          <h2>Stay sharp. On purpose.</h2>
          <p>
            The <strong>KinezioLetter</strong> is where I share the protocols, tools, and body hacks I use with private clients:
            the ones that keep engineers sharp, sleeping well, and doing their best work.
          </p>
          <p className="small-print">No fluff. Unsubscribe anytime.</p>
          <button className="btn btn-primary" onClick={openNewsletter}>
            Subscribe to the KinezioLetter
          </button>
        </div>
      </section>
    </>
  );
};

export default Home;
